import React, { useState, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { SidebarMenuItemProps } from '../../../types/layout'
import { CUSTOM_ICONS } from '../../../config/menuConfig'
import SidebarSubmenu from './SidebarSubmenu'

/**
 * Componente para un item individual del menú del sidebar
 * Incluye animaciones y soporte para submenús
 */
const isPathInMenuTree = (pathname: string, menuItem: { path: string; submenu?: { path: string; submenu?: unknown[] }[] }): boolean => {
  if (pathname === menuItem.path || pathname.startsWith(menuItem.path)) {
    return true
  }
  return menuItem.submenu?.some((sub) => isPathInMenuTree(pathname, sub as { path: string; submenu?: { path: string; submenu?: unknown[] }[] })) || false
}

const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({
  item,
  isCollapsed,
  isActive,
  isOpen,
  onToggle,
  hoveredItem,
  onHover,
  isMenuOpen,
  onToggleMenu,
}) => {
  const location = useLocation()
  const [isHovered, setIsHovered] = useState(false)
  const [isSubmenuHovered, setIsSubmenuHovered] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasSubmenu = item.submenu && item.submenu.length > 0
  const isHoveredState = hoveredItem === item.id || isHovered || isSubmenuHovered

  // Verificar si algún subitem (o anidado) está activo
  const isSubmenuActive = hasSubmenu
    ? item.submenu?.some((subItem) => isPathInMenuTree(location.pathname, subItem))
    : false

  const isItemActive = isActive || isSubmenuActive

  // Manejar icono personalizado (Falabella, MercadoLibre)
  const renderIcon = () => {
    const customIcon = CUSTOM_ICONS[item.id]
    if (customIcon) {
      return (
        <img
          src={customIcon.src}
          alt={customIcon.alt}
          className="h-5 w-5 object-contain"
        />
      )
    }
    const IconComponent = item.icon
    return (
      <IconComponent
        className={`h-5 w-5 transition-all duration-200 ${
          isItemActive
            ? 'text-custom-blue scale-110'
            : isHoveredState
            ? 'text-gray-700 scale-105'
            : 'text-gray-600'
        }`}
      />
    )
  }

  const handleMouseEnter = () => {
    // Limpiar timeout si existe
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsHovered(true)
    if (isCollapsed && hasSubmenu) {
      onHover(item.id)
    }
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    if (isCollapsed && hasSubmenu) {
      // Pequeño delay para permitir movimiento al submenú
      timeoutRef.current = setTimeout(() => {
        if (!isSubmenuHovered) {
          onHover(null)
        }
      }, 150)
    } else if (isCollapsed) {
      onHover(null)
    }
  }

  const handleSubmenuMouseEnter = () => {
    // Limpiar timeout si existe
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsSubmenuHovered(true)
    if (isCollapsed && hasSubmenu) {
      setIsHovered(true)
      onHover(item.id)
    }
  }

  const handleSubmenuMouseLeave = () => {
    setIsSubmenuHovered(false)
    if (isCollapsed && hasSubmenu) {
      setIsHovered(false)
      timeoutRef.current = setTimeout(() => {
        onHover(null)
      }, 150)
    }
  }

  // Limpiar timeout al desmontar
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  if (!hasSubmenu) {
    // Item simple sin submenú
    return (
      <li
        className="relative group"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Link
          to={item.path}
          className={`flex items-center p-3 rounded-lg transition-all duration-200 ${
            isCollapsed ? 'justify-center' : ''
          } ${
            isItemActive
              ? 'bg-blue-50 text-custom-blue shadow-sm'
              : 'text-gray-600 hover:bg-gray-50 hover:shadow-md hover:scale-[1.02]'
          } active:scale-[0.98]`}
        >
          {renderIcon()}
          {!isCollapsed && (
            <span className="ml-3 font-semibold animate-fade-in">{item.label}</span>
          )}
        </Link>

        {/* Tooltip cuando está colapsado */}
        {isCollapsed && isHoveredState && (
          <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-50 animate-slide-right whitespace-nowrap">
            {item.label}
            {item.badge && (
              <span className="ml-2 px-2 py-0.5 bg-red-500 rounded-full text-xs">
                {item.badge}
              </span>
            )}
          </div>
        )}
      </li>
    )
  }

  // Item con submenú
  return (
    <li
      className="relative overflow-visible"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        onClick={() => !isCollapsed && onToggle()}
        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all duration-200 ${
          isCollapsed ? 'justify-center' : ''
        } ${
          isItemActive
            ? isCollapsed
              ? 'bg-blue-50 text-custom-blue shadow-sm'
              : 'bg-gray-100 text-gray-700 shadow-sm'
            : 'text-gray-600 hover:bg-gray-50 hover:shadow-md hover:scale-[1.02]'
        } active:scale-[0.98]`}
      >
        <div className={`flex items-center ${isCollapsed ? 'm-auto' : ''}`}>
          {renderIcon()}
          {!isCollapsed && (
            <span className="ml-3 font-semibold animate-fade-in">{item.label}</span>
          )}
        </div>
        {!isCollapsed && (
          <ChevronDownIcon
            className={`h-4 w-4 transform transition-all duration-200 ${
              isOpen ? 'rotate-180' : ''
            } ${isItemActive ? 'text-custom-blue' : 'text-gray-400'}`}
          />
        )}
      </div>

      {/* Submenú */}
      {((!isCollapsed && isOpen) || (isCollapsed && isHoveredState)) && item.submenu && (
        <div
          onMouseEnter={handleSubmenuMouseEnter}
          onMouseLeave={handleSubmenuMouseLeave}
          className={isCollapsed ? 'absolute left-full top-0' : ''}
          style={isCollapsed ? { height: '100%' } : undefined}
        >
          <SidebarSubmenu
            items={item.submenu}
            isCollapsed={isCollapsed}
            isOpen={(!isCollapsed && isOpen) || (isCollapsed && isHoveredState)}
            parentId={item.id}
            hoveredItem={hoveredItem}
            isMenuOpen={isMenuOpen}
            onToggle={onToggleMenu}
          />
        </div>
      )}

      {/* Tooltip cuando está colapsado - solo si NO tiene submenú */}
      {isCollapsed && isHoveredState && !hasSubmenu && (
        <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-50 animate-slide-right whitespace-nowrap">
          {item.label}
        </div>
      )}
    </li>
  )
}

export default React.memo(SidebarMenuItem)

